/**
 * AdvertiserCommissionPage — commission rule list for a single advertiser.
 *
 * Layout:
 *   - Header (GREEN accent, zinc borders).
 *   - Advertiser filter input (drives the server query + on-client prefix filter).
 *   - Rate preview card — interactive subtotal input to show computed payout
 *     against the highest-priority active rule.
 *   - Rule list — ordered by priority DESC / effective_from DESC (server order).
 *
 * All network access is via ./api; this file uses no raw `fetch()`.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAdvertiserCommissionList,
	useAdvertiserCommissionDelete,
} from "./hooks";
import {
	computePayoutCents,
	filterRulesByAdvertiser,
	formatRatePreview,
	type AdvertiserCommissionRule,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const PILL_PERCENT =
	"inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-green-800 ring-1 ring-inset ring-green-200";
const PILL_FIXED =
	"inline-flex items-center rounded-full bg-zinc-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-700 ring-1 ring-inset ring-zinc-200";

function formatCents(cents: number): string {
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(cents);
	const whole = Math.floor(abs / 100);
	const frac = (abs % 100).toString().padStart(2, "0");
	return `${sign}$${whole}.${frac}`;
}

function formatUnixDate(seconds: number | null): string {
	if (seconds === null || !Number.isFinite(seconds)) return "open-ended";
	const d = new Date(seconds * 1000);
	return d.toISOString().slice(0, 10);
}

export function AdvertiserCommissionPage() {
	const [advertiserId, setAdvertiserId] = useState("");
	const [submittedAdvertiser, setSubmittedAdvertiser] = useState("");
	const [filterText, setFilterText] = useState("");
	const [subtotalDollars, setSubtotalDollars] = useState("100.00");

	const listQuery = useAdvertiserCommissionList(submittedAdvertiser);
	const deleteMut = useAdvertiserCommissionDelete();

	const rules: AdvertiserCommissionRule[] = listQuery.data?.rules ?? [];
	const visibleRules = useMemo(
		() => filterRulesByAdvertiser(rules, filterText),
		[rules, filterText],
	);

	// "Active" = first rule after server ordering (priority DESC, eff_from DESC).
	const activeRule = visibleRules[0] ?? null;

	const subtotalCents = useMemo(() => {
		const n = Number(subtotalDollars);
		if (!Number.isFinite(n) || n < 0) return 0;
		return Math.round(n * 100);
	}, [subtotalDollars]);

	const previewPayoutCents = useMemo(
		() => (activeRule ? computePayoutCents(activeRule, subtotalCents) : 0),
		[activeRule, subtotalCents],
	);

	const handleAdvertiserSubmit = (e: FormEvent) => {
		e.preventDefault();
		setSubmittedAdvertiser(advertiserId.trim());
	};

	return (
		<main
			data-testid="advertiser-commission-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					advertiser-commission
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					Advertiser commission rules
				</h1>
				<p className="text-sm text-zinc-600">
					Per-advertiser payout overrides. Rules are applied in priority order;
					the highest-priority active rule wins.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Advertiser filter"
				data-testid="advertiser-commission-filter-card"
			>
				<h2 className="text-base font-semibold text-zinc-900">Advertiser</h2>
				<form
					onSubmit={handleAdvertiserSubmit}
					className="mt-3 flex flex-col gap-3 sm:flex-row"
					data-testid="advertiser-commission-query-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="advertiser_id (e.g. adv_123)"
						value={advertiserId}
						onChange={(e) => setAdvertiserId(e.currentTarget.value)}
						data-testid="advertiser-commission-advertiser-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={!advertiserId.trim()}
						data-testid="advertiser-commission-query-submit"
					>
						Load rules
					</button>
				</form>
				<div className="mt-3">
					<label
						htmlFor="advertiser-commission-filter"
						className="block text-xs font-medium uppercase tracking-wider text-zinc-500"
					>
						Filter loaded rules
					</label>
					<input
						id="advertiser-commission-filter"
						type="text"
						className={`${INPUT} mt-1`}
						placeholder="substring match on advertiser_id"
						value={filterText}
						onChange={(e) => setFilterText(e.currentTarget.value)}
						data-testid="advertiser-commission-filter-input"
					/>
				</div>
			</section>

			<section
				className={CARD}
				aria-label="Rate preview"
				data-testid="advertiser-commission-preview-card"
			>
				<h2 className="text-base font-semibold text-zinc-900">Rate preview</h2>
				{activeRule ? (
					<div className="mt-3 grid gap-4 sm:grid-cols-2">
						<div>
							<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
								Active rule
							</p>
							<p
								className="mt-1 text-lg font-semibold text-zinc-900"
								data-testid="advertiser-commission-active-rate"
							>
								{formatRatePreview(activeRule)}
							</p>
							<p className="mt-1 text-xs text-zinc-600">
								<span
									className={
										activeRule.rule_type === "percent"
											? PILL_PERCENT
											: PILL_FIXED
									}
								>
									{activeRule.rule_type}
								</span>{" "}
								· priority {activeRule.priority} · from{" "}
								{formatUnixDate(activeRule.effective_from)}
							</p>
						</div>
						<div>
							<label
								htmlFor="advertiser-commission-subtotal"
								className="block text-xs font-medium uppercase tracking-wider text-zinc-500"
							>
								Subtotal ($)
							</label>
							<input
								id="advertiser-commission-subtotal"
								type="number"
								min="0"
								step="0.01"
								className={`${INPUT} mt-1`}
								value={subtotalDollars}
								onChange={(e) => setSubtotalDollars(e.currentTarget.value)}
								data-testid="advertiser-commission-subtotal-input"
							/>
							<p className="mt-2 text-xs text-zinc-600">
								Computed payout
							</p>
							<p
								className="text-lg font-semibold text-green-700"
								data-testid="advertiser-commission-preview-payout"
							>
								{formatCents(previewPayoutCents)} {activeRule.currency}
							</p>
						</div>
					</div>
				) : (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-commission-preview-none"
					>
						Load rules for an advertiser to preview payouts.
					</p>
				)}
			</section>

			<section
				className={CARD}
				aria-label="Commission rule list"
				data-testid="advertiser-commission-list-card"
			>
				<div className="flex items-baseline justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Rules</h2>
					<p className="text-xs text-zinc-500">
						{visibleRules.length} of {rules.length}
					</p>
				</div>
				{!submittedAdvertiser ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-commission-idle"
					>
						Enter an advertiser id above to load rules.
					</p>
				) : listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-commission-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : visibleRules.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-commission-empty"
					>
						No rules for this advertiser.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="advertiser-commission-list"
					>
						{visibleRules.map((rule) => (
							<li
								key={rule.id}
								className="flex items-center justify-between py-3 text-sm"
								data-testid={`advertiser-commission-row-${rule.id}`}
							>
								<div className="flex flex-col gap-0.5">
									<div className="flex items-center gap-2">
										<span
											className={
												rule.rule_type === "percent"
													? PILL_PERCENT
													: PILL_FIXED
											}
										>
											{rule.rule_type}
										</span>
										<span className="font-mono text-xs text-zinc-600">
											{rule.advertiser_id}
										</span>
									</div>
									<div className="text-sm font-semibold text-zinc-900">
										{formatRatePreview(rule)}
									</div>
									<div className="text-xs text-zinc-500">
										priority {rule.priority} · {formatUnixDate(rule.effective_from)}
										{" → "}
										{formatUnixDate(rule.effective_to)}
									</div>
								</div>
								<button
									type="button"
									className={BTN_GHOST}
									onClick={() => deleteMut.mutate(rule.id)}
									disabled={deleteMut.isPending}
									data-testid={`advertiser-commission-delete-${rule.id}`}
								>
									Delete
								</button>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AdvertiserCommissionPage;
